import { Injectable, inject } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { CrowdctrlToastService } from './crowdctrl-toast.service';
import { BehaviorSubject, Observable, Subscription, filter, interval } from 'rxjs';

/**
 * Service to handle PWA updates with notification-based strategy.
 * Checks for updates on app load and periodically, shows Ionic toast notifications
 * when updates are available, and allows users to reload to get the new version.
 */
@Injectable({
  providedIn: 'root',
})
export class PwaUpdateService {
  private swUpdate = inject(SwUpdate);
  private toastService = inject(CrowdctrlToastService);
  private checkInterval = 30 * 1000; // 30 seconds in milliseconds
  private periodicCheckSubscription: Subscription | null = null;
  private initialized = false;
  /** True while an update toast is visible or being created (prevents duplicate sticky toasts). */
  private updateToastActive = false;
  private activeUpdateToast: HTMLIonToastElement | null = null;

  /** Whether an update is available */
  private updateAvailableSubject = new BehaviorSubject<boolean>(false);
  public updateAvailable$: Observable<boolean> = this.updateAvailableSubject.asObservable();

  /** Whether we're currently checking for updates */
  private isCheckingSubject = new BehaviorSubject<boolean>(false);
  public isChecking$: Observable<boolean> = this.isCheckingSubject.asObservable();

  /** Whether an update is being downloaded (found but not yet ready) */
  private updateDownloadingSubject = new BehaviorSubject<boolean>(false);
  public updateDownloading$: Observable<boolean> = this.updateDownloadingSubject.asObservable();

  /** Whether the service worker is enabled */
  get isEnabled(): boolean {
    return this.swUpdate.isEnabled;
  }

  /**
   * Initialize the update service.
   * Should be called on app startup.
   */
  initialize(): void {
    if (!this.swUpdate.isEnabled) {
      console.log('Service Worker is not enabled');
      return;
    }

    if (this.initialized) {
      return;
    }
    this.initialized = true;

    // Check for updates immediately
    this.checkForUpdates();

    // Listen for version updates (single subscription — avoid duplicate toasts if initialize ran twice)
    this.swUpdate.versionUpdates
      .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
      .subscribe(() => {
        this.updateDownloadingSubject.next(false);
        this.updateAvailableSubject.next(true);
        void this.showUpdateNotification();
      });

    // Start periodic checking
    this.startPeriodicChecking();
  }

  /**
   * Manually check for updates.
   * @returns Promise that resolves to true if an update is available
   */
  async checkForUpdates(): Promise<boolean> {
    if (!this.swUpdate.isEnabled) {
      return false;
    }

    this.isCheckingSubject.next(true);
    try {
      const updateAvailable = await this.swUpdate.checkForUpdate();
      if (updateAvailable) {
        console.log('Update available, downloading...');
        this.updateDownloadingSubject.next(true);
      }
      return updateAvailable;
    } catch (error) {
      console.error('Error checking for updates:', error);
      return false;
    } finally {
      this.isCheckingSubject.next(false);
    }
  }

  /**
   * Start periodic update checking.
   * Checks every 30 seconds by default.
   */
  startPeriodicChecking(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    if (this.periodicCheckSubscription) {
      return;
    }

    this.periodicCheckSubscription = interval(this.checkInterval).subscribe(() => {
      this.checkForUpdates();
    });
  }

  /**
   * Stop periodic update checking.
   */
  stopPeriodicChecking(): void {
    if (this.periodicCheckSubscription) {
      this.periodicCheckSubscription.unsubscribe();
      this.periodicCheckSubscription = null;
    }
  }

  /**
   * Show update notification toast.
   * At most one sticky “update available” toast: dismiss any previous and ignore re-entrancy.
   */
  private async showUpdateNotification(): Promise<void> {
    if (this.updateToastActive) {
      return;
    }
    this.updateToastActive = true;

    try {
      if (this.activeUpdateToast) {
        try {
          await this.activeUpdateToast.dismiss();
        } catch {
          /* already detached */
        }
        this.activeUpdateToast = null;
      }

      const el = await this.toastService.create({
        header: 'Time to level up',
        message: 'A new version is available',
        sticky: true,
        showDismissButton: false,
        buttons: [
          {
            text: 'Apply',
            role: 'reload',
            handler: () => {
              void this.reloadApp();
            },
          },
          {
            text: 'Later',
            role: 'cancel',
          },
        ],
      });

      this.activeUpdateToast = el;
      el.onDidDismiss().then(() => {
        this.activeUpdateToast = null;
        this.updateToastActive = false;
      });

      await el.present();
    } catch (error) {
      console.error('Error showing update toast:', error);
      this.updateToastActive = false;
      this.activeUpdateToast = null;
    }
  }

  /**
   * Reload the app to apply updates.
   */
  async reloadApp(): Promise<void> {
    // Reset update available state before reload
    this.updateAvailableSubject.next(false);
    if (this.activeUpdateToast) {
      try {
        await this.activeUpdateToast.dismiss();
      } catch {
        /* noop */
      }
      this.activeUpdateToast = null;
    }
    this.updateToastActive = false;

    if (!this.swUpdate.isEnabled) {
      window.location.reload();
      return;
    }

    try {
      await this.swUpdate.activateUpdate();
      window.location.reload();
    } catch (error) {
      console.error('Error activating update:', error);
      // Fallback to regular reload
      window.location.reload();
    }
  }

  /**
   * Get current update available state synchronously
   */
  get updateAvailable(): boolean {
    return this.updateAvailableSubject.getValue();
  }

  /**
   * Get current checking state synchronously
   */
  get isChecking(): boolean {
    return this.isCheckingSubject.getValue();
  }

  /**
   * Get current update downloading state synchronously
   */
  get updateDownloading(): boolean {
    return this.updateDownloadingSubject.getValue();
  }
}
